import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const tyresoPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-tyreso",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Tyresö – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Tyresö. Vi hämtar möbler, vitvaror och skräp i Bollmora, Trollbäcken, Tyresö strand, Krusboda och Östra Tyresö. Fast pris, ofta samma dag.",
      h1: "Bortforsling Tyresö",
    },

    hero: {
      heading: "Bortforsling i Tyresö",
      intro:
        "Boka bortforsling i Tyresö – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Bollmora och Granängsringen till villorna i Trollbäcken, Tyresö strand och de gamla fritidshusen på Brevikshalvön. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Tyresö – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Tyresö",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Tyresö och körs till kretsloppscentralen i Petterboda",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Trollbäcken, Tyresö",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Bollmora, Tyresö",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Tyresö strand, Tyresö",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Krusboda, Tyresö",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Östra Tyresö, Brevikshalvön",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Tyresö – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Tyresö är en varierad kommun sydost om Stockholm, indelad i fyra delar: Bollmora i nordväst med Tyresö centrum, Granängsringen och Krusboda, det villatäta Trollbäcken i väster, Tyresö strand i öster med Brevikshalvön och Tyresö slott, samt Östra Tyresö i sydost – en del av den inre skärgården där många gamla fritidshus i dag är permanentbostäder, granne med Tyresta nationalpark. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, medan det vid kusten och i skärgårdsdelarna kan handla om smala grusvägar och hus med branta tomter. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt fritidshus, lastar i bilen och kör till närmaste återvinningscentral. Tyresö har en stor fördel här – kommunens egen kretsloppscentral i Petterboda vid Strömfallsvägen ligger mitt i kommunen. Det gör att avfallet sällan behöver köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTyresö saknar tåg och tunnelbana, så hela kommunen nås via väg – framför allt Tyresövägen och Gudöbroleden, med goda bussförbindelser till Gullmarsplan. Vi känner vägnätet väl och planerar rutten därefter, vilket gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både höghusen i Bollmora och de smala vägarna ut mot Brevik och Trinntorp – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Tyresö",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Trollbäcken, Tyresö strand eller på Brevikshalvön är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas, och om vägen fram är grusad eller brant. Bor du i lägenhet i Bollmora, Granängsringen eller Krusboda, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Tyresöbo på kretsloppscentralen i Petterboda.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Tyresö",
          content:
            "Vi utför bortforsling i hela Tyresö kommun – från lägenhetskvarteren till skärgårdsbebyggelsen. Oavsett om du bor i en lägenhet i Bollmora, en villa i Trollbäcken, ett radhus i Tyresö strand eller ett permanentat fritidshus i Östra Tyresö kan vi vara på plats, ofta redan samma dag.\n\nI Bollmora – med Tyresö centrum, Granängsringen, Krusboda och Fårdala – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, i allt från lägre flerbostadshus till de högre punkthusen. I Trollbäcken möter vi nästan uteslutande villabebyggelse med garage och förråd.\n\nVi är minst lika snabba i kommunens östra och södra delar: Tyresö strand med Brevikshalvön, Tyresö slott och kyrkan, samt Östra Tyresö med Brevik, Trinntorp och Dyvik ut mot Tyresta nationalpark. I dessa skärgårdsnära områden är vi vana vid smala vägar och tomter nära vattnet och planerar alltid hur skåpbilen ska komma fram. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Tyresö",
          content:
            "En viktig del av vår bortforsling i Tyresö är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Tyresö har en stor fördel eftersom kommunen har en egen station mitt i kommunen:\n\nKretsloppscentralen i Petterboda vid Strömfallsvägen – Tyresös egen återvinningscentral, där sorterat grovavfall, elavfall, vitvaror, trädgårdsavfall och farligt avfall tas emot, liksom föremål för återbruk. Eftersom Petterboda ligger centralt i kommunen håller vi restiden – och därmed kostnaden – nere. Kommunen erbjuder även mobil miljöstation och säsongshämtning av grovavfall, men vi tar hand om hela jobbet åt dig direkt.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Tyresö med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Tyresös kommundelar, från höghusen i Bollmora till villatomterna i Trollbäcken och de smala vägarna på Brevikshalvön, och vet att kretsloppscentralen i Petterboda ligger centralt. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och når Tyresö smidigt via Tyresövägen och Gudöbroleden. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Tyresö, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via kretsloppscentralen i Petterboda. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Tyresö?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Tyresö.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Tyresö?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Tyresö?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och når Tyresö via Tyresövägen och Gudöbroleden, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Tyresö?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Tyresö?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Bollmora, Granängsringen och Krusboda. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och fritidshus i Tyresö?",
        answer:
          "Ja, en stor del av våra uppdrag i Tyresö är villahämtningar i Trollbäcken och Tyresö strand samt vid permanentade fritidshus på Brevikshalvön och i Östra Tyresö. Vi backar in på uppfarten där det går, även på grusade och branta vägar, och tömmer garage, källare och förråd.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Tyresö försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Tyresö?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Tyresö har en egen station, kretsloppscentralen i Petterboda vid Strömfallsvägen, mitt i kommunen. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Tyresö på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Tyresö?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Trollbäcken och lägenhetsförråden i Bollmora och Krusboda.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Tyresö?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Bollmora och ditt fritidshus på Brevikshalvön.",
      },
      {
        question: "Vilka kommundelar i Tyresö täcker ni?",
        answer:
          "Vi utför bortforsling i hela Tyresö – Bollmora (Tyresö centrum, Granängsringen, Krusboda, Fårdala), Trollbäcken, Tyresö strand med Brevikshalvön samt Östra Tyresö med Brevik, Trinntorp och Dyvik. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Tyresö?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Tyresöbo på kretsloppscentralen i Petterboda.",
      },
      {
        question: "Vilka kommuner runt Tyresö utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Tyresö i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Tyresö kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Tyresö idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Tyresö kommun.",
    },

    en: {
      slug: "junk-removal-tyreso",
      meta: {
        title: "Junk Removal Tyresö – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Tyresö. We pick up furniture, appliances and bulky items in Bollmora, Trollbäcken, Tyresö strand, Krusboda and Östra Tyresö. Fixed price, often same day.",
        h1: "Junk Removal Tyresö",
      },
      hero: {
        heading: "Junk Removal in Tyresö",
        intro:
          "Book junk removal in Tyresö – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Bollmora and Granängsringen to the villas in Trollbäcken, Tyresö strand and the former summer cottages on the Brevik peninsula. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Tyresö – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Tyresö",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Tyresö and driven to the Petterboda recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Trollbäcken, Tyresö",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Bollmora, Tyresö",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Tyresö strand, Tyresö",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Krusboda, Tyresö",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Östra Tyresö, Brevik peninsula",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Tyresö – fast, hassle-free and responsible",
        mainText:
          "Tyresö is a varied municipality southeast of Stockholm, divided into four parts: Bollmora in the northwest with Tyresö centrum, Granängsringen and Krusboda; the villa-dense Trollbäcken in the west; Tyresö strand in the east with the Brevik peninsula and Tyresö Castle; and Östra Tyresö in the southeast – part of the inner archipelago where many former summer cottages are now permanent homes, next to Tyresta National Park. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, while along the coast and in the archipelago parts it can be narrow gravel roads and houses on steep plots. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or summer cottage, load them into the van and drive to the nearest recycling centre. Tyresö has a big advantage here – the municipality's own recycling centre in Petterboda on Strömfallsvägen is located in the middle of the municipality. This means the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nTyresö has no train or metro, so the whole municipality is reached by road – above all Tyresövägen and Gudöbroleden, with good bus links to Gullmarsplan. We know the road network well and plan the route accordingly, which means we can often be on site the same day you book. Our workers are used to both the high-rises of Bollmora and the narrow roads out towards Brevik and Trinntorp – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Tyresö",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Trollbäcken, Tyresö strand or on the Brevik peninsula, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening, and whether the access road is gravelled or steep. If you live in an apartment in Bollmora, Granängsringen or Krusboda, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Tyresö resident you can drop those off for free at the Petterboda recycling centre.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Tyresö",
            content:
              "We perform junk removal across the entire municipality of Tyresö – from the apartment quarters to the archipelago housing. Whether you live in an apartment in Bollmora, a villa in Trollbäcken, a townhouse in Tyresö strand or a converted summer cottage in Östra Tyresö, we can be on site, often the same day.\n\nIn Bollmora – with Tyresö centrum, Granängsringen, Krusboda and Fårdala – we are highly experienced with apartment pickups via elevator and stairwell, in everything from lower apartment blocks to the taller point houses. In Trollbäcken we meet almost exclusively villa housing with garages and storage rooms.\n\nWe are just as quick in the eastern and southern parts of the municipality: Tyresö strand with the Brevik peninsula, Tyresö Castle and the church, and Östra Tyresö with Brevik, Trinntorp and Dyvik out towards Tyresta National Park. In these archipelago-edge areas we are used to narrow roads and waterside plots and always plan how the van can get through. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Tyresö",
            content:
              "An important part of our junk removal in Tyresö is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Tyresö has a big advantage because the municipality has its own station in the middle of the municipality:\n\nThe Petterboda recycling centre on Strömfallsvägen – Tyresö's own recycling centre, which receives sorted bulky waste, e-waste, white goods, garden waste and hazardous waste, as well as items for reuse. Because Petterboda is centrally located in the municipality, we keep travel time – and therefore the cost – down. The municipality also offers a mobile environmental station and seasonal bulky-waste collection, but we take care of the whole job for you directly.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Tyresö with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Tyresö's districts, from the high-rises of Bollmora to the villa plots of Trollbäcken and the narrow roads of the Brevik peninsula, and that the Petterboda recycling centre is centrally located. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and reach Tyresö smoothly via Tyresövägen and Gudöbroleden. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Tyresö, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via the Petterboda recycling centre. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Tyresö cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Tyresö.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Tyresö?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Tyresö?",
          answer:
            "We have vans and staff across the Stockholm region and reach Tyresö via Tyresövägen and Gudöbroleden, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Tyresö?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Tyresö?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Bollmora, Granängsringen and Krusboda. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and summer cottages in Tyresö?",
          answer:
            "Yes, a large share of our jobs in Tyresö are villa pickups in Trollbäcken and Tyresö strand as well as converted summer cottages on the Brevik peninsula and in Östra Tyresö. We reverse onto the driveway where possible, even on gravelled and steep roads, and clear garages, basements and storage rooms.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Tyresö insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Tyresö?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Tyresö has its own station, the Petterboda recycling centre on Strömfallsvägen, in the middle of the municipality. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Tyresö on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Tyresö?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the villa storage rooms in Trollbäcken and the apartment storage units in Bollmora and Krusboda.",
        },
        {
          question: "Can you make multiple stops during junk removal in Tyresö?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Bollmora and your summer cottage on the Brevik peninsula, for example.",
        },
        {
          question: "Which districts in Tyresö do you cover?",
          answer:
            "We perform junk removal across all of Tyresö – Bollmora (Tyresö centrum, Granängsringen, Krusboda, Fårdala), Trollbäcken, Tyresö strand with the Brevik peninsula, and Östra Tyresö with Brevik, Trinntorp and Dyvik. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Tyresö?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Tyresö resident you can drop those off for free at the Petterboda recycling centre.",
        },
        {
          question: "Which municipalities around Tyresö do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Tyresö in 3 easy steps",
        step2Text:
          "Our young workers in Tyresö come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Tyresö today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Tyresö.",
      },
    },
  },
];
